import * as URL from '@hotjar/insights-common/lib/url';
import { RULE_TRIGGER_TYPE } from '@hotjar/feedback/domain/common/TargetingRule/TargetingRule';
import { noop } from '@hotjar/webapp/lib/function';
import type { PageTargetEntry } from '../../../../common/Wizard/common/targeting';

const validatorsForRuleType = {
  trigger: (rule: PageTargetEntry) => {
    if (/[^\w- /]/gi.test(rule.target.pattern)) {
      return 'invalid-trigger-pattern';
    }
    return undefined;
  },
  url: (rule: PageTargetEntry) => {
    const urlPattern = rule.target.pattern;
    switch (rule.target.match_operation) {
      case 'simple':
      case 'exact':
        if (!URL.validateHTTPSUrl(urlPattern)) {
          return 'invalid-url-format';
        }
        return undefined;
      case 'starts_with':
        if (!URL.validateHTTPSUrl(urlPattern)) {
          return 'invalid-url-format';
        }
        return undefined;
      case 'regex':
        try {
          RegExp(urlPattern);
        } catch (e) {
          return 'invalid-regex';
        }
        return undefined;
      case 'ends_with':
      case 'contains':
        return undefined;
      default:
        throw new Error('Unhandled match expression');
    }
  },
  attribute: noop,
  device: noop,
};

const normalizersForRuleType = {
  trigger: (rule: PageTargetEntry) => {
    return rule.target.pattern.trim();
  },
  url: (rule: PageTargetEntry) => {
    switch (rule.target.match_operation) {
      case 'simple':
      case 'exact':
      case 'starts_with':
        return URL.normalize(rule.target.pattern);
      case 'regex':
      case 'ends_with':
      case 'contains':
        return rule.target.pattern;
      default:
        throw new Error('Unhandled match expression');
    }
  },
  [RULE_TRIGGER_TYPE.ATTRIBUTE]: noop,
  [RULE_TRIGGER_TYPE.DEVICE]: noop,
};

export function validateRuleAndNormalize(rule: PageTargetEntry, normalize = true): PageTargetEntry {
  // Exit early if the rule pattern is empty.
  if (
    rule.target.pattern === undefined ||
    (typeof rule.target.pattern === 'string' && rule.target.pattern.trim().length === 0)
  ) {
    return { ...rule, error: 'cannot-be-empty' };
  }

  // max pattern length
  if (typeof rule.target.pattern === 'string' && rule.target.pattern.length > 1000) {
    return { ...rule, error: 'invalid-pattern-too-long' };
  }

  // Rule-specific validation.
  const error = validatorsForRuleType[rule.target.component](rule);

  const target =
    error === undefined && normalize
      ? { ...rule.target, pattern: normalizersForRuleType[rule.target.component](rule) }
      : rule.target;

  return {
    ...rule,
    error,
    target,
  };
}
